import type { LinkIntent, LinkKind } from './deepLink';

// ==============================|| SHARE LINKS — WHERE EACH RESOURCE LIVES ||==================== //
//
// `/go/{kind}/{id}` names a RESOURCE; this table is what turns it into a path in one of the SPAs.
//
// ⚠ IT USED TO LIVE IN apps/login, AND MOVED HERE FOR THE NOTIFICATION DRAWER (NOTIF-DRAWER phase 2). The
// login origin needs every row at once (it resolves a pasted link for a reader it knows nothing about);
// the header bell's "Open" needs ITS OWN app's row, so a click inside the admin console navigates inside
// the admin console instead of bouncing through the login origin. One table, two readers — a second copy
// in each SPA would be four tables that drift the first time a route is renamed.
//
// ⚠ THE SAME RESOURCE HAS DIFFERENT PATHS IN DIFFERENT APPS, which is the reason this is a table rather
// than a passthrough. An event is `/events/:id` for a student and a teacher, but the admin console has no
// read-only event page at all — its equivalent is the edit form under `/education/events/:id/edit`.
//
// ⚠ ORDER MATTERS AND IS THE FALLBACK, NOT THE RULE: see `appForLink` in apps/login/src/links.
//
// ⚠ A ROW WHOSE PATH IGNORES THE ID IS A LIST PAGE. Some resources have no page of their own in some app
// (one exam for a student, one payment document for an admin); the row then opens the list the item is
// in, rather than inventing a route.

/** The SPAs a link can open in. */
export type LinkApp = 'student' | 'teacher' | 'admin';

/** One place a kind can be read, and the path it is read at. */
export interface LinkCandidate {
  app: LinkApp;
  path: (id: string) => string;
}

/**
 * ⚠ EVERY KIND IN `LINK_KINDS` NEEDS A ROW, and apps/login's `linkTargets.test.ts` fails the build if one
 * does not. A kind with no row parses fine and then resolves to nothing, which reaches the reader as
 * "this link isn't available for your account".
 */
export const LINK_TARGETS: Record<LinkKind, readonly LinkCandidate[]> = {
  event: [
    { app: 'student', path: (id) => `/events/${id}` },
    { app: 'teacher', path: (id) => `/events/${id}` },
    // No read-only event page in the admin console — the edit form IS the detail view there.
    { app: 'admin', path: (id) => `/education/events/${id}/edit` }
  ],
  order: [
    { app: 'student', path: (id) => `/orders/${id}` },
    { app: 'teacher', path: (id) => `/orders/${id}` },
    { app: 'admin', path: (id) => `/commerce/orders/${id}` }
  ],
  ticket: [
    { app: 'student', path: (id) => `/support/${id}` },
    { app: 'teacher', path: (id) => `/support/${id}` },
    // Operations owns tickets on the admin side; the student's "support request" is the same row.
    { app: 'admin', path: (id) => `/operations/tickets/${id}` }
  ],
  // EXAM-NOTIFY: the "Open the exam" button in the enrolled-student mail (Notification writes /go/exam/{id}).
  exam: [
    // ⚠ The student app has no page for ONE exam — its Exams page lists the papers the student can sit, and
    // that is where this one is. The id is dropped on purpose rather than invented into a route.
    { app: 'student', path: () => '/exams' },
    // No teacher row: the teacher app has no exam pages, so a teacher reading the mail is told the link is
    // not available for their account — which is true.
    { app: 'admin', path: (id) => `/education/exams/${id}` }
  ],
  // LRN-REM: the 'Open the course' button in the live-lesson reminder (Notification writes /go/course/{id}).
  // The student joins a live lesson from the course page, because the Join click IS the attendance.
  course: [
    { app: 'student', path: (id) => `/courses/${id}` },
    { app: 'teacher', path: (id) => `/my-courses/${id}` },
    { app: 'admin', path: (id) => `/education/courses/${id}` }
  ],
  // NOTIF-DRAWER: a payer's receipt waiting for review. Admin-only, and there is no page for ONE document —
  // the approvals queue is where it is decided.
  payment_document: [{ app: 'admin', path: () => '/commerce/payment-approvals' }],
  // NOTIF-DRAWER: a role request or public role application to decide. Admin-only; the queue is the page.
  role_request: [{ app: 'admin', path: () => '/identity/role-requests' }],
  // NOTIF-DRAWER: an appointment reminder. Every app lists appointments; none has a page for one.
  appointment: [
    { app: 'student', path: () => '/appointments' },
    { app: 'teacher', path: () => '/appointments' },
    { app: 'admin', path: () => '/education/appointments' }
  ],
  // TCH-TASKS: a task assigned to you / due soon / overdue. Both task pages open the drawer from ?task=. No student row:
  // a student is never an assignee.
  task: [
    { app: 'teacher', path: (id) => `/tasks?task=${id}` },
    { app: 'admin', path: (id) => `/operations/tasks?task=${id}` }
  ],
  // SALES: a quote (expired notice, a shared link). Staff only — quotes live in the admin console.
  quote: [{ app: 'admin', path: (id) => `/commerce/quotes/${id}` }]
};

/**
 * The path this intent opens at INSIDE `app`, or null when the resource has no page there. For in-app
 * navigation by a reader who is already in that app (the notification drawer); a reader the caller knows
 * nothing about goes through `/go/` instead.
 */
export function linkPathIn(app: LinkApp, intent: LinkIntent): string | null {
  return LINK_TARGETS[intent.kind].find((candidate) => candidate.app === app)?.path(intent.id) ?? null;
}
